import { createSelector } from '@reduxjs/toolkit';

import { getFeatureState, getTaskCountFromTasks } from '@app/selectors';
import type { RootState } from '@app/types';

import type { NavigationItem } from '../../../../types/navigation';
import { ProjectType } from '../../../../types/project';
import type { ProjectsTasksBadges } from '../../types';

const getProjectByProjectId = (state: RootState, projectId: string) =>
    state.projects.projectsList.list.find((item) => item.id === projectId);

const getTaskBadges = (state: RootState): ProjectsTasksBadges | null => state.projects.badges;

const getTaskCount = createSelector([getProjectByProjectId, getTaskBadges], (project, taskBadges) =>
    getTaskCountFromTasks(taskBadges || undefined, project?.id, project?.branches?.currentId),
);

const getisFilesDownloadEnabled = (state: RootState) => !!getFeatureState(state, 'lionFeatureFilesManagementDownload');

const makeNavigationMapSelector = () =>
    createSelector(
        [getProjectByProjectId, getTaskCount, getisFilesDownloadEnabled],
        (project, taskCount, isFilesDownloadEnabled): NavigationItem[] => {
            if (!project) {
                return [];
            }

            const { id, type, branches, isAdmin, isBranchingEnabled, offlineTranslation, adminRights } = project;

            const isFilesButtonVisible = isFilesDownloadEnabled && project.type === ProjectType.pagedDocuments;

            let navigationMapVisibleSections: NavigationItem[] = [
                {
                    key: 'tasks',
                    label: 'Tasks',
                    link: `/tasks/${id}/`,
                    visible: isAdmin,
                    disabled: !adminRights?.tasks,
                    badge: taskCount,
                    icon: 'tasks',
                },
                {
                    key: 'contributors',
                    label: 'Contributors',
                    link: `/contributors/${id}/`,
                    visible: isAdmin,
                    disabled: !adminRights?.contributors,
                    icon: 'contributors',
                },
                {
                    key: 'download',
                    label: 'Download',
                    link: `/download/${id}/`,
                    visible: isAdmin && !isFilesButtonVisible,
                    disabled: !adminRights?.download,
                    icon: 'download',
                },
                {
                    key: 'files',
                    label: 'Files',
                    link: `/files/${id}/`,
                    visible: isFilesButtonVisible,
                    icon: 'files',
                },
            ];

            let navigationMapMoreSections = [
                {
                    label: 'Editor',
                    link: `/project/${id}/`,
                    visible: true,
                },
                {
                    label: 'Upload',
                    link: `/upload/${id}/`,
                    visible: isAdmin && adminRights?.upload,
                },
                {
                    label: 'Screenshots',
                    link: `/screenshots/${id}/`,
                    visible: isAdmin && adminRights?.screenshots,
                },
                {
                    label: 'Glossary',
                    link: `/glossary/${id}/`,
                    visible: true,
                },
                {
                    label: 'Apps',
                    link: `/apps/${id}/`,
                    visible: true,
                },
            ];

            if (project.type === ProjectType.contentIntegration) {
                navigationMapVisibleSections = navigationMapVisibleSections.filter(({ key }) => key !== 'download');
                navigationMapMoreSections = navigationMapMoreSections.filter(({ label }) => label !== 'Upload');
            }

            return [
                ...navigationMapVisibleSections,
                {
                    key: 'more',
                    label: 'More...',
                    visible: isAdmin || offlineTranslation,
                    icon: 'more',
                    dropdown: [
                        navigationMapMoreSections,
                        [
                            {
                                link: `/settings/${id}/`,
                                label: 'Settings',
                                visible: isAdmin && adminRights?.settings,
                            },
                            {
                                link: `/settings/${id}/#branches`,
                                label: 'Branches',
                                visible: isAdmin && adminRights?.settings && isBranchingEnabled,
                            },
                            {
                                link: `/dupes/${id}/`,
                                label: 'Duplicate finder',
                                visible: isAdmin && adminRights?.manageKeys && type === ProjectType.localizationFiles,
                            },
                            {
                                link: `/settings/${id}/#project-automations`,
                                label: 'Automations',
                                visible: isAdmin && adminRights?.settings,
                            },
                        ],
                        [
                            {
                                label: 'Activity',
                                visible: offlineTranslation || adminRights?.activity,
                                onClick: () => {
                                    const showActivityEvent = new CustomEvent('show-activity', {
                                        detail: {
                                            projectId: id,
                                            branchId: branches?.currentId,
                                            branchName: branches?.currentName,
                                        },
                                    });
                                    document.dispatchEvent(showActivityEvent);
                                },
                            },
                            {
                                link: `/statistics/${id}/`,
                                label: 'Statistics',
                                visible: isAdmin && adminRights?.statistics,
                            },
                        ],
                    ],
                },
            ];
        },
    );

export default makeNavigationMapSelector;
